import axios, { AxiosInstance } from 'axios';

import { AWSConfig } from 'Config/AWSConfig';
import { ACKNOWLEDGEMENT, ASSESSMENTS, DETAILS, DISCUSSION, FRAMEWORKS, GROUPS, LOGIN, PUBLIC, REGISTER, REVIEW, SERVICES, THIRD_PARTIES, TPRM } from 'Config/Paths';
import { SaveQuestionnaireControlGroupRequest } from 'Models/DueDiligence';
import { ResponseModel } from 'Models/ResponseModel';
import { PublicAddDiscussionMessageRequest, PublicClientAndThirdPartyServiceDetails, PublicQuestionnaire, PublicQuestionnaireRegistrationRequest } from 'Models/TPRM';
import { publicRestApiGet, publicRestApiPost } from 'PublicRestApiConnector';

import { TprmQuestionnaireApi } from './TprmQuestionnaireApi';

export class AWSTprmQuestionnaireApi implements TprmQuestionnaireApi {
    axiosInstance: AxiosInstance;

    constructor() {
        this.axiosInstance = axios.create();
        this.axiosInstance.defaults.baseURL = AWSConfig.apiGateway.URL;

        this.axiosInstance.interceptors.response.use(
            (response) => {
                return response.data;
            },
            (error) => {
                return Promise.reject(error);
            }
        );
    }

    register = async (request: PublicQuestionnaireRegistrationRequest): Promise<ResponseModel<void>> => {
        try {
            return await this.axiosInstance.post(`${PUBLIC}/${TPRM}/${REGISTER}`, request);
        } catch (error) {
            console.error(error.response);
            throw new Error(error.response.data.message);
        }
    };

    checkIfAuthorized = async (credentials: string): Promise<ResponseModel<void>> => {
        try {
            const headers = { Authorization: `Basic ${credentials}` };
            return await this.axiosInstance.get(`${PUBLIC}/${TPRM}/${LOGIN}`, { headers: headers });
        } catch (error) {
            console.error(error.response);
            throw new Error(error.response.data.message);
        }
    };

    getAllQuestionnairesForService = (thirdPartyId: string, serviceId: string): Promise<ResponseModel<PublicQuestionnaire[]>> => {
        return publicRestApiGet(`/${PUBLIC}/${TPRM}/${THIRD_PARTIES}/${thirdPartyId}/${SERVICES}/${serviceId}/${ASSESSMENTS}`, {});
    };
    getClientAndThirdPartyDetails = (): Promise<ResponseModel<PublicClientAndThirdPartyServiceDetails>> => {
        return publicRestApiGet(`/${PUBLIC}/${TPRM}/${DETAILS}`, {});
    };
    getQuestionnaire = (thirdPartyId: string, serviceId: string, assessmentId: string): Promise<ResponseModel<PublicQuestionnaire>> => {
        return publicRestApiGet(`/${PUBLIC}/${TPRM}/${THIRD_PARTIES}/${thirdPartyId}/${SERVICES}/${serviceId}/${ASSESSMENTS}/${assessmentId}`, {});
    };

    addDiscussionMessage = (thirdPartyId: string, serviceId: string, assessmentId: string, request: PublicAddDiscussionMessageRequest): Promise<ResponseModel<PublicQuestionnaire>> => {
        return publicRestApiPost(`/${PUBLIC}/${TPRM}/${THIRD_PARTIES}/${thirdPartyId}/${SERVICES}/${serviceId}/${ASSESSMENTS}/${assessmentId}/${DISCUSSION}`, request);
    };
    markDiscussionAsRead = (thirdPartyId: string, serviceId: string, assessmentId: string): Promise<ResponseModel<PublicQuestionnaire>> => {
        return publicRestApiPost(`/${PUBLIC}/${TPRM}/${THIRD_PARTIES}/${thirdPartyId}/${SERVICES}/${serviceId}/${ASSESSMENTS}/${assessmentId}/${DISCUSSION}/${ACKNOWLEDGEMENT}`, {});
    };
    saveControlGroup = (thirdPartyId: string, serviceId: string, assessmentId: string, controlFramework: string, controlGroup: string, request: SaveQuestionnaireControlGroupRequest): Promise<ResponseModel<PublicQuestionnaire>> => {
        return publicRestApiPost(`/${PUBLIC}/${TPRM}/${THIRD_PARTIES}/${thirdPartyId}/${SERVICES}/${serviceId}/${ASSESSMENTS}/${assessmentId}/${FRAMEWORKS}/${controlFramework}/${GROUPS}/${controlGroup}`, request);
    };
    submitQuestionnaire = (thirdPartyId: string, serviceId: string, assessmentId: string): Promise<ResponseModel<PublicQuestionnaire>> => {
        return publicRestApiPost(`/${PUBLIC}/${TPRM}/${THIRD_PARTIES}/${thirdPartyId}/${SERVICES}/${serviceId}/${ASSESSMENTS}/${assessmentId}/${REVIEW}`, {});
    };
}
